import { useEffect, useRef } from "react";
import { getCallStatusColor } from "../utils/callStatusOptions";

/**
 * Anchored-popover (desktop) / bottom-sheet (mobile) showing call-status options.
 *
 * @param {string[]}  options      List of call status values to show
 * @param {DOMRect}   anchorRect   getBoundingClientRect() of the clicked badge
 * @param {Function}  onSelect     (statusValue) => void
 * @param {Function}  onClose      () => void
 * @param {boolean}   isMobile     render as bottom-sheet
 */
export default function CallStatusActionMenu({ options, anchorRect, onSelect, onClose, isMobile }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [onClose]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!options || options.length === 0) return null;

  /* ── Mobile: bottom-sheet ── */
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30">
        <div
          ref={menuRef}
          className="w-full max-w-lg bg-white rounded-t-2xl shadow-2xl pb-6 pt-3 px-4"
          style={{ animation: "slideUp .2s ease-out" }}
        >
          <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mb-3" />
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
            Update Call Status
          </p>
          <div className="space-y-1">
            {options.map((opt) => (
              <button
                key={opt}
                onClick={() => { onSelect(opt); onClose(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getCallStatusColor(opt).replace(/text-\S+/, "").trim()}`} />
                {opt}
              </button>
            ))}
          </div>
          <button
            onClick={onClose}
            className="w-full mt-3 py-2.5 text-sm font-medium text-gray-500 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  /* ── Desktop: anchored dropdown ── */
  let top = 0;
  let left = 0;
  if (anchorRect) {
    top = anchorRect.bottom + 4;
    left = anchorRect.left;
    const menuWidth = 220;
    const menuHeight = options.length * 36 + 36;
    if (left + menuWidth > window.innerWidth) left = window.innerWidth - menuWidth - 8;
    if (top + menuHeight > window.innerHeight) top = anchorRect.top - menuHeight - 4;
    if (left < 4) left = 4;
  }

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[200px]"
        style={{ position: "fixed", top, left }}
      >
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 pt-2 pb-1.5">
          Update Call Status
        </p>
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => { onSelect(opt); onClose(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getCallStatusColor(opt).replace(/text-\S+/, "").trim()}`} />
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}
